import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, IdCard, Mail, Phone, Store as StoreIcon, Users as UsersIcon, FileUp, BarChart3, FileText, FolderTree, Receipt } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { staffApi, storesApi, brandsApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SesameImportDialog from '../components/SesameImportDialog';
import FreshaImportDialog from '../components/FreshaImportDialog';
import FreshaUnmatchedBanner from '../components/FreshaUnmatchedBanner';
import PayslipBulkDialog from '../components/PayslipBulkDialog';
import { StaffFoldersManager } from '../components/StaffFoldersManager';
import StoreMultiSelector from '../components/StoreMultiSelector';
import api from '../lib/api';

// Mansione visibile nella card. NON confondere con `role` utente (RBAC).
const ROLE_LABELS = {
  operations_manager: 'Operations Manager',
  hr_manager: 'HR Manager',
  area_manager: 'Area Manager',
  store_manager: 'Store Manager',
  barber_senior: 'Barbiere Senior',
  barber_junior: 'Barbiere Junior',
  apprentice: 'Apprendista',
  receptionist: 'Reception',
  other: 'Altro',
};
const STATUS_LABELS = {
  active: 'Attivo',
  on_leave: 'In permesso',
  sick: 'In malattia',
  maternity: 'Maternità',
  terminated: 'Cessato',
};
const STATUS_TONE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_leave: 'bg-blue-50 text-blue-700 border-blue-200',
  sick: 'bg-amber-50 text-amber-700 border-amber-200',
  maternity: 'bg-pink-50 text-pink-700 border-pink-200',
  terminated: 'bg-slate-100 text-slate-500 border-slate-200',
};

function initials(staff) {
  return `${(staff.first_name || '?')[0] || ''}${(staff.last_name || '?')[0] || ''}`.toUpperCase();
}

function tenureLabel(hireDate) {
  if (!hireDate) return null;
  const start = new Date(hireDate);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 1) return 'Nuovo arrivo';
  if (months < 12) return `${months} mes${months === 1 ? 'e' : 'i'} in azienda`;
  const years = Math.floor(months / 12);
  return `${years} ann${years === 1 ? 'o' : 'i'} in azienda`;
}

export default function StaffPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showPayslips, setShowPayslips] = useState(false);
  const [showSesame, setShowSesame] = useState(false);
  const [showFresha, setShowFresha] = useState(false);
  const [activeTab, setActiveTab] = useState('people');

  const isOps = user?.role === 'operations_manager';
  const isHR = user?.role === 'hr_manager';
  const isAM = user?.role === 'area_manager';
  const isSM = user?.role === 'store_manager';
  // AM/SM senza store visibili → nascondiamo il bottone Crea (otterrebbero 403).
  // OPS/HR possono creare ovunque, anche con `stores` momentaneamente vuoto in UI.
  const hasAnyStore = stores.length > 0;
  const canCreate = isOps || isHR || ((isAM || isSM) && hasAnyStore);
  const canImportSesame = isOps || isHR;
  const canViewMonthlyReport = isOps || isHR;
  // Tab "Cartelle documenti": gestione centralizzata del template cartelle
  // staff (struttura globale, si propaga a tutti i dipendenti). Solo OPS/HR.
  const canManageFolders = isOps || isHR;

  const handleMonthlyReportPreview = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/monthly-report/preview', { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error('Errore nel generare il report mensile');
    }
  }, []);

  const handleMonthlyReportSendNow = useCallback(async () => {
    if (!window.confirm('Invio del report HR mensile a tutti i destinatari (OPS, HR Manager, Area Manager). Procedere?')) return;
    try {
      const { data } = await staffApi.monthlyReportTrigger();
      toast.success(`Report mensile · inviati: ${data.sent}, saltati: ${data.skipped}, errori: ${data.failed}`);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st, br, us] = await Promise.all([
        // include_terminated=true: il backend di default filtra fuori i cessati;
        // qui li vogliamo nella lista per poterli filtrare/visualizzare in UI
        // (fix 2026-06-04 — l'utente segnalava che lo staff cessato non appariva
        // quando filtrava su "Cessato" dal dropdown stato).
        staffApi.list({ include_terminated: true }).then(r => r.data),
        storesApi.list().then(r => r.data),
        brandsApi.list().then(r => r.data).catch(() => []),
        // OPS only: lista users per linkare staff↔account (AM/SM non vedono /users).
        isOps ? usersApi.list().then(r => r.data).catch(() => []) : Promise.resolve([]),
      ]);
      setStaff(s || []);
      setStores(Array.isArray(st) ? st : (st?.stores || []));
      setBrands(br || []);
      setUsers(us || []);
    } catch (e) {
      toast.error('Errore nel caricamento dello staff');
    } finally {
      setLoading(false);
    }
  }, [isOps]);

  useEffect(() => { load(); }, [load]);

  const storeMap = useMemo(
    () => Object.fromEntries(stores.map(s => [s.store_id, s.display_name || s.name])),
    [stores]
  );
  const brandMap = useMemo(
    () => Object.fromEntries(brands.map(b => [b.brand_id, b.name])),
    [brands]
  );

  const filtered = useMemo(() => {
    return staff.filter(p => {
      if (filterBrand && p.brand_id !== filterBrand) return false;
      if (filterStore && p.store_id !== filterStore && !(p.secondary_store_ids || []).includes(filterStore)) return false;
      if (filterRole && p.role_in_store !== filterRole) return false;
      // Status filter:
      //  - se selezionato → match esatto (incluso "Cessato" che fa vedere SOLO i cessati)
      //  - se vuoto (default) → nascondi i cessati per non diluire la vista
      if (filterStatus) {
        if (p.status !== filterStatus) return false;
      } else if (p.status === 'terminated') {
        return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${p.first_name} ${p.last_name} ${p.display_name || ''} ${p.email_work || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [staff, filterBrand, filterStore, filterRole, filterStatus, search]);

  const kpis = useMemo(() => {
    const active = staff.filter(p => p.status === 'active').length;
    const storesActive = new Set(staff.filter(p => p.status === 'active').map(p => p.store_id)).size;
    const byRole = {};
    for (const p of staff) {
      if (p.status === 'terminated') continue;
      byRole[p.role_in_store] = (byRole[p.role_in_store] || 0) + 1;
    }
    return { total: staff.length, active, storesActive, byRole };
  }, [staff]);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="staff-page">
      <FreshaUnmatchedBanner />
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center font-bold rounded-sm">
            <IdCard className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Gestione Staff</h1>
            <p className="text-slate-500 text-sm mt-0.5">Anagrafica e profili dei collaboratori</p>
          </div>
        </div>
        {canCreate && activeTab === 'people' && (
          <div className="flex gap-2 flex-wrap">
            {canImportSesame && (
              <>
                <Button variant="outline" onClick={() => setShowSesame(true)} className="gap-2" data-testid="sesame-import-btn">
                  <FileUp className="w-4 h-4" /> Importa anagrafica (SesameHR)
                </Button>
                <Button variant="outline" onClick={() => setShowFresha(true)} className="gap-2" data-testid="fresha-import-btn">
                  <BarChart3 className="w-4 h-4" /> Importa performance (Fresha)
                </Button>
              </>
            )}
            {canViewMonthlyReport && (
              <>
                <Button variant="outline" onClick={handleMonthlyReportPreview} className="gap-2" data-testid="monthly-report-preview-btn">
                  <FileText className="w-4 h-4" /> Anteprima Report HR
                </Button>
                <Button variant="outline" onClick={handleMonthlyReportSendNow} className="gap-2" data-testid="monthly-report-send-btn">
                  <Mail className="w-4 h-4" /> Invia Report HR ora
                </Button>
              </>
            )}
            <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="staff-new-btn">
              <Plus className="w-4 h-4" /> Nuovo dipendente
            </Button>
          </div>
        )}
      </div>

      {/* Tab switcher: Dipendenti | Cartelle documenti (solo OPS/HR) */}
      {canManageFolders && (
        <div className="flex gap-1 border-b border-slate-200 mb-6" data-testid="staff-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('people')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === 'people' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="staff-tab-people"
          >
            <UsersIcon className="w-4 h-4" /> Dipendenti
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === 'folders' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="staff-tab-folders"
          >
            <FolderTree className="w-4 h-4" /> Cartelle documenti
          </button>
        </div>
      )}

      {activeTab === 'folders' && canManageFolders ? (
        <div className="space-y-4">
          <div className="max-w-3xl flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowPayslips(true)} className="gap-1.5" data-testid="payslip-bulk-btn">
              <Receipt className="w-3.5 h-3.5" /> Carica buste paga
            </Button>
          </div>
          <StaffFoldersManager />
          {showPayslips && <PayslipBulkDialog onClose={() => setShowPayslips(false)} />}
        </div>
      ) : (
        <>
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="staff-stats">
        <StatTile label="Totale dipendenti" value={kpis.total} testid="kpi-total" />
        <StatTile label="Attivi" value={kpis.active} testid="kpi-active" />
        <StatTile label="Negozi coinvolti" value={kpis.storesActive} testid="kpi-stores-active" />
        <StatTile label="Barbieri Senior" value={kpis.byRole.barber_senior || 0} testid="kpi-barber-senior" />
      </div>

      {/* Search + filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, cognome, email…"
            className="pl-9"
            data-testid="staff-search"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterBrand || 'all'} onValueChange={(v) => setFilterBrand(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-auto min-w-[160px] h-9" data-testid="filter-brand"><SelectValue placeholder="Tutti i brand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i brand</SelectItem>
              {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStore || 'all'} onValueChange={(v) => setFilterStore(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-auto min-w-[160px] h-9" data-testid="filter-store"><SelectValue placeholder="Tutti i negozi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i negozi</SelectItem>
              {stores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterRole || 'all'} onValueChange={(v) => setFilterRole(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-auto min-w-[160px] h-9" data-testid="filter-role"><SelectValue placeholder="Tutte le mansioni" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le mansioni</SelectItem>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-auto min-w-[140px] h-9" data-testid="filter-status"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterBrand || filterStore || filterRole || filterStatus) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterBrand(''); setFilterStore(''); setFilterRole(''); setFilterStatus(''); }} data-testid="filters-reset">
              Reset filtri
            </Button>
          )}
        </div>
      </div>

      {/* Empty / Grid */}
      {filtered.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-12 text-center" data-testid="staff-empty">
          <UsersIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nessun dipendente trovato</p>
          <p className="text-slate-500 text-sm mt-1">
            {staff.length === 0
              ? 'Aggiungi il primo dipendente o importa il CSV da SesameHR.'
              : 'Modifica i filtri o cerca con altri termini.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="staff-grid">
          {filtered.map(p => (
            <StaffCard
              key={p.staff_id}
              staff={p}
              storeName={storeMap[p.store_id] || '—'}
              brandName={brandMap[p.brand_id] || ''}
              secondaryNames={(p.secondary_store_ids || []).map(id => storeMap[id]).filter(Boolean)}
              onClick={() => navigate(`/staff/${p.staff_id}`)}
            />
          ))}
        </div>
      )}
        </>
      )}

      {showCreate && (
        <CreateStaffDialog
          stores={stores}
          brands={brands}
          users={users}
          existingStaff={staff}
          onClose={() => setShowCreate(false)}
          onCreated={(newStaff) => { setShowCreate(false); load(); navigate(`/staff/${newStaff.staff_id}`); }}
          isOps={isOps}
        />
      )}

      {showSesame && (
        <SesameImportDialog
          stores={stores}
          onClose={() => setShowSesame(false)}
          onImported={() => { setShowSesame(false); load(); }}
        />
      )}

      {showFresha && (
        <FreshaImportDialog
          staffMembers={staff}
          onClose={() => setShowFresha(false)}
          onImported={() => { setShowFresha(false); load(); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
      <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

function StaffCard({ staff, storeName, brandName, secondaryNames = [], onClick }) {
  const tone = STATUS_TONE[staff.status] || STATUS_TONE.active;
  const tenure = tenureLabel(staff.hire_date);
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-slate-200 hover:border-slate-400 transition-colors rounded-sm p-4 flex flex-col gap-3"
      data-testid={`staff-card-${staff.staff_id}`}
    >
      <div className="flex items-start gap-3">
        {staff.photo_url ? (
          <img src={staff.photo_url} alt="" className="w-12 h-12 rounded-full object-cover bg-slate-100" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
            {initials(staff)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{staff.first_name} {staff.last_name}</p>
          <p className="text-xs text-slate-500 truncate">{ROLE_LABELS[staff.role_in_store] || staff.role_in_store}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm shrink-0 ${tone}`}>
          {STATUS_LABELS[staff.status] || staff.status}
        </span>
      </div>
      <div className="text-xs text-slate-600 space-y-1">
        <div className="flex items-center gap-1.5">
          <StoreIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="truncate">{storeName}{brandName ? ` · ${brandName}` : ''}</span>
        </div>
        {secondaryNames.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid={`staff-secondary-stores-${staff.staff_id}`}>
            <StoreIcon className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <span className="truncate text-slate-400" title={`Negozi aggiuntivi: ${secondaryNames.join(', ')}`}>
              Anche: {secondaryNames.join(', ')}
            </span>
          </div>
        )}
        {staff.email_work && (
          <div className="flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{staff.email_work}</span>
          </div>
        )}
        {staff.phone_work && (
          <div className="flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{staff.phone_work}</span>
          </div>
        )}
      </div>
      {tenure && (
        <p className="text-[11px] text-slate-400 mt-auto">{tenure}</p>
      )}
    </button>
  );
}

function CreateStaffDialog({ stores, brands, users = [], existingStaff = [], onClose, onCreated, isOps }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '',
    role_in_store: 'barber_senior',
    brand_id: brands[0]?.brand_id || '',
    store_id: stores[0]?.store_id || '',
    secondary_store_ids: [],
    hire_date: new Date().toISOString().slice(0, 10),
    email_work: '', phone_work: '',
    status: 'active',
    user_id: '',
  });
  const [saving, setSaving] = useState(false);

  // Filtro utenti: nascondi quelli già linkati a una scheda staff
  const linkedUserIds = useMemo(
    () => new Set(existingStaff.map(s => s.user_id).filter(Boolean)),
    [existingStaff]
  );
  const availableUsers = useMemo(
    () => users.filter(u => !linkedUserIds.has(u.user_id)),
    [users, linkedUserIds]
  );

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Quando si linka a un account, prefilliamo nome/email/mansione coerenti col ruolo.
  const selectUser = (userId) => {
    if (!userId || userId === 'none') {
      setForm(prev => ({ ...prev, user_id: '' }));
      return;
    }
    const u = users.find(x => x.user_id === userId);
    if (!u) return;
    const [first, ...rest] = (u.name || '').trim().split(/\s+/);
    setForm(prev => ({
      ...prev,
      user_id: userId,
      // Pre-fill solo se i campi sono vuoti (rispetto editing manuale)
      first_name: prev.first_name || first || '',
      last_name: prev.last_name || rest.join(' ') || '',
      email_work: prev.email_work || u.email || '',
      role_in_store: prev.role_in_store === 'barber_senior' && u.role ? u.role : prev.role_in_store,
    }));
  };

  // Auto-sync brand quando si cambia store
  const handleStoreChange = (storeId) => {
    const st = stores.find(s => s.store_id === storeId);
    setForm(prev => ({
      ...prev,
      store_id: storeId,
      brand_id: st?.brand_id || prev.brand_id,
      // Il nuovo primario non può restare anche tra i secondari.
      secondary_store_ids: prev.secondary_store_ids.filter(id => id !== storeId),
    }));
  };

  async function save() {
    if (!form.first_name || !form.last_name) {
      toast.error('Nome e cognome obbligatori');
      return;
    }
    if (!form.store_id || !form.brand_id) {
      toast.error('Brand e negozio obbligatori');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Campi vuoti → undefined per non passare stringhe vuote al backend
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const r = await staffApi.create(payload);
      toast.success('Dipendente creato');
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione dipendente');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="create-staff-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo dipendente</DialogTitle>
          <DialogDescription>Compila i campi base per creare la scheda. I dati sensibili potranno essere aggiunti dopo (solo OPS).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {isOps && availableUsers.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="user-link-block">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Collega ad account utente (opzionale)</Label>
              <Select value={form.user_id || 'none'} onValueChange={selectUser}>
                <SelectTrigger className="mt-1.5" data-testid="select-user-link"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun collegamento</SelectItem>
                  {availableUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name || u.email} {u.role ? `· ${u.role.replace(/_/g, ' ')}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Linkando un utente, vedrà la propria scheda da <span className="font-mono">/my-profile</span>.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Nome *</Label>
              <Input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="input-first-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Cognome *</Label>
              <Input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="input-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Mansione *</Label>
              <Select value={form.role_in_store} onValueChange={(v) => update('role_in_store', v)}>
                <SelectTrigger data-testid="select-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Negozio *</Label>
              <Select value={form.store_id} onValueChange={handleStoreChange}>
                <SelectTrigger data-testid="select-store"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {stores.length > 1 && (
            <div>
              <Label className="text-xs uppercase tracking-wide">Negozi aggiuntivi (opzionale)</Label>
              <div className="mt-1.5">
                <StoreMultiSelector
                  stores={stores}
                  brands={brands}
                  value={form.secondary_store_ids}
                  onChange={(ids) => update('secondary_store_ids', ids)}
                  excludeStoreId={form.store_id}
                  testIdPrefix="create-secondary-store"
                  maxHeight="max-h-40"
                  groupByBrand
                  helperText="Per dipendenti che ruotano su più negozi. Il negozio principale resta quello selezionato sopra."
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Data assunzione</Label>
              <Input type="date" value={form.hire_date || ''} onChange={(e) => update('hire_date', e.target.value)} data-testid="input-hire-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Stato</Label>
              <Select value={form.status} onValueChange={(v) => update('status', v)}>
                <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).filter(([v]) => v !== 'terminated').map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Email aziendale</Label>
              <Input value={form.email_work} onChange={(e) => update('email_work', e.target.value)} placeholder="nome@littleitalybarbershop.it" data-testid="input-email-work" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Telefono aziendale</Label>
              <Input value={form.phone_work} onChange={(e) => update('phone_work', e.target.value)} data-testid="input-phone-work" />
            </div>
          </div>
          {isOps && (
            <p className="text-xs text-slate-500 pt-2 border-t">
              I dati sensibili (codice fiscale, IBAN, contratto, ecc.) potrai aggiungerli dopo dalla scheda dipendente — sono visibili solo a te (OPS).
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="create-staff-submit">{saving ? 'Salvataggio…' : 'Crea dipendente'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
